// Synthesized audio — zero binary assets (nothing to license, host, or squeeze through the
// discordsays CSP), all cues built from oscillators + filtered noise at call time.
//
// Rules of the room:
// - The AudioContext unlocks on the first user gesture (autoplay policy); until then every
//   cue is a silent no-op. Spins are clicks, so the first tick always has permission.
// - Ticks are UNIFORM: pitch/gain derive from angular velocity alone, never from which
//   wedge is passing. An audio near-miss (a special sound as gold slides by) is banned the
//   same as a geometric one.
// - golden_ticket's sting is SILENCE — the one tier that says nothing is the loudest.
// - Mute is persisted (localStorage) and defaults to sound ON.

const STORE_KEY = 'arcade:audio';

class ArcadeAudio {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;
  private muted = false;
  // Cues that arrived while the context was still resuming — resume() is async, and
  // silently dropping everything until it settles is how an entire first spin goes mute.
  private pendingCues: (() => void)[] = [];

  constructor() {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(STORE_KEY);
      if (raw) this.muted = JSON.parse(raw)?.muted === true;
    } catch {
      // storage unavailable (private mode) — default to sound on, no persistence
    }
    // First gesture anywhere unlocks the context (capture so it beats stopPropagation).
    window.addEventListener('pointerdown', () => this.ensure(), { capture: true, once: true });
    // Discord mobile webviews suspend the context after backgrounding; resume on return.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.ctx?.state === 'suspended') {
        void this.ctx.resume();
      }
    });
  }

  getMuted(): boolean {
    return this.muted;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    try {
      window.localStorage.setItem(STORE_KEY, JSON.stringify({ muted }));
    } catch {
      // best-effort persistence only
    }
  }

  private ensure(): AudioContext | null {
    if (typeof window === 'undefined') return null;
    if (!this.ctx) {
      const Ctor = window.AudioContext ?? (window as never as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return null;
      this.ctx = new Ctor();
      this.master = this.ctx.createGain();
      this.master.gain.value = 0.5;
      this.master.connect(this.ctx.destination);
    }
    if (this.ctx.state === 'suspended') {
      void this.ctx.resume().then(() => this.flushPending());
    }
    return this.ctx;
  }

  private flushPending(): void {
    if (this.ctx?.state !== 'running') return;
    const queued = this.pendingCues.splice(0, this.pendingCues.length);
    for (const cue of queued) cue();
  }

  // Debug surface: painted into the boot log / readable from the toggle handler.
  state(): string {
    return `${this.ctx?.state ?? 'no-ctx'}/${this.muted ? 'muted' : 'on'}`;
  }

  // Run the cue now if the context runs; if it is mid-resume, park it briefly (a cue
  // older than the park window is stale theater — capped, and cleared on flush).
  private emit(cue: () => void): void {
    if (this.muted) return;
    const ctx = this.ensure();
    if (!ctx || !this.master) return;
    if (ctx.state === 'running') {
      cue();
      return;
    }
    if (this.pendingCues.length < 24) this.pendingCues.push(cue);
  }

  private live(): { ctx: AudioContext; master: GainNode } | null {
    if (this.muted) return null;
    const ctx = this.ensure();
    if (!ctx || !this.master || ctx.state !== 'running') return null;
    return { ctx, master: this.master };
  }

  private noise(ctx: AudioContext): AudioBuffer {
    if (!this.noiseBuffer) {
      const buffer = ctx.createBuffer(1, ctx.sampleRate * 0.2, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < data.length; i++) data[i] = Math.random() * 2 - 1;
      this.noiseBuffer = buffer;
    }
    return this.noiseBuffer;
  }

  private blip(
    freq: number,
    opts: { type?: OscillatorType; gain?: number; attack?: number; decay?: number; at?: number; glideTo?: number } = {},
  ): void {
    const live = this.live();
    if (!live) return;
    const { ctx, master } = live;
    const { type = 'square', gain = 0.12, attack = 0.002, decay = 0.06, at = 0, glideTo } = opts;
    const t0 = ctx.currentTime + at;
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    if (glideTo !== undefined) osc.frequency.exponentialRampToValueAtTime(Math.max(1, glideTo), t0 + decay);
    env.gain.setValueAtTime(0, t0);
    env.gain.linearRampToValueAtTime(gain, t0 + attack);
    env.gain.exponentialRampToValueAtTime(0.0001, t0 + attack + decay);
    osc.connect(env).connect(master);
    osc.start(t0);
    osc.stop(t0 + attack + decay + 0.02);
  }

  private thud(gain: number, at = 0): void {
    const live = this.live();
    if (!live) return;
    const { ctx, master } = live;
    const t0 = ctx.currentTime + at;
    const src = ctx.createBufferSource();
    src.buffer = this.noise(ctx);
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 320;
    const env = ctx.createGain();
    env.gain.setValueAtTime(gain, t0);
    env.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.12);
    src.connect(filter).connect(env).connect(master);
    src.start(t0);
    src.stop(t0 + 0.14);
  }

  // The peg tick. `crossed` boundaries this frame; pitch follows angular velocity ONLY —
  // identical for every wedge by design (see header).
  tick(crossed: number, velocity: number): void {
    this.emit(() => {
      const speed = Math.min(1, Math.abs(velocity) / 14); // ~launch velocity → 1
      for (let i = 0; i < Math.min(crossed, 3); i++) {
        this.blip(900 + 700 * speed, { type: 'square', gain: 0.045 + 0.035 * speed, decay: 0.025, at: i * 0.012 });
      }
    });
  }

  // Landing thunk — wood on wood.
  thunk(): void {
    this.emit(() => {
      this.blip(180, { type: 'sine', gain: 0.22, decay: 0.16, glideTo: 70 });
      this.thud(0.18);
    });
  }

  // The sealed-envelope beat while the settle round-trip runs: a quiet rising tone.
  riser(): void {
    this.emit(() => {
      this.blip(220, { type: 'sine', gain: 0.05, attack: 0.05, decay: 0.55, glideTo: 440 });
    });
  }

  // Audible self-test: fired by the sound toggle so "is audio alive?" is one tap away.
  confirm(): void {
    this.emit(() => {
      this.blip(880, { type: 'triangle', gain: 0.12, decay: 0.09 });
    });
  }

  // Per-tier reveal sting. Ordinal ladder: the offering is soft and low, the pot is the only
  // fanfare, golden is silence.
  sting(tier: string): void {
    this.emit(() => this.stingNow(tier));
  }

  private stingNow(tier: string): void {
    switch (tier) {
      case 'nothing': // two descending low notes — the offering settles
        this.blip(196, { type: 'sine', gain: 0.1, decay: 0.18 });
        this.blip(147, { type: 'sine', gain: 0.09, decay: 0.24, at: 0.14 });
        break;
      case 'fs_5_25': // one coin, returned
        this.blip(1245, { type: 'triangle', gain: 0.09, decay: 0.1 });
        this.blip(1661, { type: 'triangle', gain: 0.07, decay: 0.14, at: 0.07 });
        break;
      case 'common_item': // a heavier object lands on the shelf
        this.thud(0.22);
        this.blip(392, { type: 'triangle', gain: 0.08, decay: 0.12, at: 0.05 });
        break;
      case 'spin_ticket': // bright single blip — the wheel re-arms
        this.blip(988, { type: 'triangle', gain: 0.1, decay: 0.08 });
        this.blip(1318, { type: 'triangle', gain: 0.1, decay: 0.16, at: 0.09 });
        break;
      case 'jackpot': { // the one fanfare: rising gold arpeggio + shimmer tail
        const notes = [523, 659, 784, 1046, 1318];
        notes.forEach((f, i) => this.blip(f, { type: 'triangle', gain: 0.12, decay: 0.3, at: i * 0.09 }));
        this.blip(2093, { type: 'sine', gain: 0.05, attack: 0.2, decay: 0.9, at: 0.5 });
        break;
      }
      case 'golden_ticket':
        // Silence. You only get to use it once.
        break;
      default:
        break;
    }
  }
}

export const audio = new ArcadeAudio();
